// File: src/pages/TicketsPage.jsx

import React, {
  useEffect,
  useState,
} from 'react';
import axios from 'axios';
import EventCard from '../components/EventCard';
import MinimalFooter from '../components/MinimalFooter';
import NoEvent from '../assets/NoEvent.svg';

import {
  getEventStartMs,
  getEventEndMs,
} from '../utils/eventTime';

function isEventOngoing(event) {
  const startMs = getEventStartMs(event);
  const endMs = getEventEndMs(event);

  if (!startMs || !endMs) {
    return false;
  }

  const now = Date.now();

  return (
    now >= startMs &&
    now <= endMs
  );
}

function isEventNotStarted(event) {
  const startMs = getEventStartMs(event);

  if (!startMs) {
    return false;
  }

  return Date.now() < startMs;
}

function isEventEnded(event) {
  const endMs = getEventEndMs(event);

  if (!endMs) {
    return false;
  }

  return Date.now() > endMs;
}

export default function TicketsPage() {
  const userId =
    localStorage.getItem('userId');

  const [events, setEvents] =
    useState([]);

  const [loaded, setLoaded] =
    useState(false);

  const [timeTick, setTimeTick] =
    useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeTick(Date.now());
    }, 30000);

    return () =>
      clearInterval(interval);
  }, []);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const { data } =
          await axios.get(
            `${process.env.REACT_APP_API_URL}/events`
          );

        setEvents(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        console.error(
          '[TicketsPage fetchEvents]',
          err
        );
      } finally {
        setLoaded(true);
      }
    }

    fetchEvents();
  }, []);

  function isUserRegistered(
    ev,
    uid
  ) {
    if (!uid) {
      return false;
    }

    const uidStr = String(uid);

    if (
      Array.isArray(ev.attendeeIds) &&
      ev.attendeeIds.length
    ) {
      return ev.attendeeIds.some(
        (id) =>
          String(id?._id ?? id) ===
          uidStr
      );
    }

    if (
      Array.isArray(ev.attendees) &&
      ev.attendees.length
    ) {
      return ev.attendees.some(
        (attendee) =>
          String(
            attendee?._id ??
              attendee
          ) === uidStr
      );
    }

    return false;
  }

  const reservedEvents = events
    .filter((ev) =>
      isUserRegistered(
        ev,
        userId
      )
    )
    .filter(
      (ev) => !isEventEnded(ev)
    );

  return (
    <div style={S.shell}>
      <main style={S.page}>
        <div style={S.inner}>
          {!loaded ? (
            <div
              style={S.statusText}
            >
              Loading your reservations...
            </div>
          ) : reservedEvents.length ===
            0 ? (
            <div style={S.empty}>
              <img
                src={NoEvent}
                alt="No reservations"
                style={S.emptyImg}
              />

              <h1
                style={S.emptyTitle}
              >
                No reservations yet
              </h1>

              <p
                style={S.emptyText}
              >
                When you reserve a spot
                at a gathering, your
                reservation will show up
                here.
              </p>

              <button
                type="button"
                style={S.emptyButton}
                onClick={() => {
                  window.location.href =
                    '/events';
                }}
              >
                Find a gathering
              </button>
            </div>
          ) : (
            <>
              <div
                style={S.listHeader}
              >
                <div>
                  <div
                    style={S.listLabel}
                  >
                    Your reservations
                  </div>

                  <div
                    style={S.listSub}
                  >
                    {
                      reservedEvents.length
                    }{' '}
                    upcoming reservation
                    {reservedEvents.length ===
                    1
                      ? ''
                      : 's'}
                  </div>
                </div>
              </div>

              <div style={S.grid}>
                {reservedEvents.map(
                  (ev) => {
                    const live =
                      isEventOngoing(
                        ev
                      );

                    const upcoming =
                      isEventNotStarted(
                        ev
                      );

                    return (
                      <EventCard
                        key={`${ev._id}-${timeTick}`}
                        event={ev}
                        userId={userId}
                        live={live}
                        upcoming={
                          upcoming
                        }
                        occupantCount={
                          ev.occupancyCount ||
                          ev.attendeeCount ||
                          0
                        }
                        isUserSaved={
                          false
                        }
                        onSaveToggle={() => {}}
                        onNavigate={() => {}}
                        onSelectEvent={() => {
                          window.location.href = `/events/${ev._id}`;
                        }}
                      />
                    );
                  }
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <MinimalFooter />
    </div>
  );
}

const S = {
  shell: {
    minHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },

  page: {
    flex: '1 0 auto',
    width: '100%',
    background: '#fff',
    padding:
      '20px 20px 36px',
    boxSizing: 'border-box',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  inner: {
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
  },

  listHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent:
      'space-between',
    gap: 16,
    margin: '0 0 12px',
    paddingBottom: 14,
  },

  listLabel: {
    color: '#111',
    fontSize: '.98rem',
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: '-0.03em',
  },

  listSub: {
    marginTop: 5,
    color:
      'rgba(17,17,17,0.52)',
    fontSize: '.82rem',
    lineHeight: 1.25,
    fontWeight: 520,
    letterSpacing:
      '-0.015em',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(250px, 1fr))',
    gap: 20,
  },

  statusText: {
    marginTop: 40,
    textAlign: 'center',
    fontSize: '.9rem',
    color:
      'rgba(17,17,17,0.58)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },

  empty: {
    minHeight: '58vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    maxWidth: 320,
    margin: '0 auto',
  },

  emptyImg: {
    width: 150,
    maxWidth: '76%',
    marginBottom: 20,
  },

  emptyTitle: {
    margin: 0,
    fontSize: '1.25rem',
    lineHeight: 1,
    fontWeight: 850,
    color: '#111',
    letterSpacing:
      '-0.045em',
  },

  emptyText: {
    maxWidth: 260,
    margin: '10px auto 0',
    fontSize: '.92rem',
    color:
      'rgba(17,17,17,0.58)',
    lineHeight: 1.45,
    fontWeight: 500,
    letterSpacing:
      '-0.012em',
  },

  emptyButton: {
    marginTop: 22,
    border: 'none',
    background: '#ddd',
    color: '#000',
    borderRadius: 999,
    padding: '12px 18px',
    minHeight: 44,
    fontSize: 14,
    fontWeight: 850,
    letterSpacing:
      '-0.018em',
    cursor: 'pointer',
    WebkitTapHighlightColor:
      'transparent',
  },
};